import type { DailyLiceCount, Last3DaysResult, WeeklyLiceCount } from '@/services/types';
import { extractDayInTimezone } from '@/utils/date';

import type {
    DomainDailyLiceCount,
    DomainLast3DaysLiceCount,
    DomainWeeklyLiceCount,
} from './types';

/**
 * Wire to domain.
 *
 * The shapes are identical today (see `types.ts`), so these do not translate fields — they make
 * the result **total**. An absent response becomes an empty array, or `null` for the snapshot,
 * rather than `undefined`, so a consumer reads `data.length` without guarding first.
 * `domain/cages` records the same reasoning for the cage overview, and it is what lets the
 * containers drop most of their optional chaining.
 *
 * They also normalise `day` to a calendar date. The endpoints declare it as `date-time` and
 * send a full ISO timestamp, while every consumer wants `YYYY-MM-DD` — the legacy chart mappers
 * each stripped the time themselves with `day.split('T')[0]`. Doing it once here is the
 * difference between an axis that reads `Sep 4` and one that prints the raw timestamp: the
 * chart layer keys its day axis on `YYYY-MM-DD` and parses it as `` `${day}T00:00:00Z` ``, so a
 * value that already carries a time produces `NaN` and falls back to showing the label verbatim.
 *
 * Rows are otherwise spread rather than copied field by field. The daily endpoint serves several
 * fields the published DTO does not declare — `cageId`, `femaleRaw` and others — and an explicit
 * copy would silently drop them. Nothing is known to read them, but this PR is not the place to
 * find out.
 */

/**
 * `2026-06-01T00:00:00.000Z` and `2026-06-01` both become `2026-06-01`.
 *
 * Read in **UTC**, which is the calendar date the `@db.Date` column holds and the instant the
 * endpoint serialises it at. Letting the helper default to the host zone would move the day:
 * `2026-06-01T00:00:00.000Z` is still May 31st in Santiago, so every Chilean reader would see
 * the whole series shifted back one day.
 *
 * A value that is already a calendar date is passed through untouched. moment parses a bare
 * `YYYY-MM-DD` as *local* midnight, so handing it to the helper would move it back a day at
 * every positive offset instead — `dayStartISOInTimezone` rejects timestamps on exactly these
 * grounds, and uses the same `T` discriminator to tell the two apart.
 */
function toCalendarDay(day: string): string {
    return day.includes('T') ? extractDayInTimezone(day, 'UTC') : day;
}

export function mapDailyLiceCounts(rows: DailyLiceCount[] | undefined): DomainDailyLiceCount[] {
    return (rows ?? []).map((row) => ({
        ...row,
        day: toCalendarDay(row.day),
        temperature: row.temperature ?? null,
    }));
}

/**
 * The assertion is the seam doing its job: the endpoint sends `day` and the generated type does
 * not declare it, so somebody has to say so once. Here, with a reason, rather than at each of
 * the places that read it.
 */
export function mapWeeklyLiceCounts(rows: WeeklyLiceCount[] | undefined): DomainWeeklyLiceCount[] {
    return (rows ?? []).map((row) => {
        // Narrowed to the one undeclared field rather than asserting the whole row, and read as
        // possibly absent: the previous blanket cast told the compiler `day` was a string, so a
        // response without it reached `toCalendarDay` as `undefined` and threw on `.includes`.
        const { day } = row as WeeklyLiceCount & { day?: string };

        return { ...row, day: day === undefined ? '' : toCalendarDay(day) };
    });
}

/**
 * `null` when the endpoint published nothing — a cage with no data, or too few fish in the
 * window for an average to be worth publishing. The caller cannot tell those apart, and never
 * could.
 */
export function mapLast3DaysLiceCount(
    row: Last3DaysResult | undefined | null
): DomainLast3DaysLiceCount | null {
    return row ? { ...row, day: toCalendarDay(row.day) } : null;
}
